import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, FormSpy } from 'react-final-form';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import ClientFields from 'components/client/ClientFields';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import validateEditClientForm from 'common/validations/form/editClient';
import { updateClientInitialValues } from 'common/form/initial-values/update';
import { buildPlanPlatformInitial, derivePlanPlatformInput } from 'common/form/planPlatform';
import getDataToSave from 'common/form/dataToSave';
import { generatePassword, getFieldsToUpdate } from 'common/form/utils';
import { clientFindFields } from 'common/constants/query-fields/client';
import requestStatus from 'common/constants/status';
import routes from 'common/routes';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import FormSurface from 'components/shared/surfaces/FormSurface';
import UserEditHeader from 'components/shared/UserEditHeader';
import AppBarContext from 'context/AppBarContext';
import geoSlice from 'store/modules/geo';
import clientSlice from 'store/modules/client';
import planSlice from 'store/modules/plan';

const requiredFields = [
  'first_name',
  'last_name',
  'email',
  'birthdate',
  'phone',
  'country',
  'state',
  'ui_language'
];

function ClientEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['buttons', 'common', 'routes']);
  const { id } = useParams();
  const pageTitle = t('client.edit', { ns: 'routes' });
  const [initialValues, setInitialValues] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const formRef = useRef(null);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const cities = useSelector((state) => state.geo.cities);
  const countries = useSelector((state) => state.geo.countries);
  const client = useSelector((state) => state.client.data.findClient);
  const geoStatus = useSelector((state) => state.geo.status);
  const planOptions = useSelector((state) => state.plan.planOptions);
  const planStatus = useSelector((state) => state.plan.status);
  const states = useSelector((state) => state.geo.states);
  const status = useSelector((state) => state.client.status.general);
  const previousStatus = React.useRef(status);

  useEffect(() => {
    dispatch(clientSlice.actions.find({ fields: clientFindFields, id }));
  }, []);

  // Espera a que carguen client Y planOptions: los multi-selects platforms/plans necesitan el
  // catálogo de planes (para identificar el plan Active Plus y clasificar primario/adicional).
  useEffect(() => {
    if (client && Object.keys(client).length > 0 && client.id === id && planOptions.length > 0) {
      const userData = updateClientInitialValues(client);
      const { platforms, plans } = buildPlanPlatformInitial(client, planOptions);
      setInitialValues({ ...userData, platforms, plans });

      if (userData.state?.value) {
        dispatch(geoSlice.actions.getCities({ state: userData.state.value }));
        setSelectedState(userData.state.value);
      }
      if (userData.country?.label) {
        dispatch(geoSlice.actions.getStates(userData.country.label));
        setSelectedCountry(userData.country.label);
      }
    }
  }, [client, planOptions]);

  useEffect(() => {
    if (geoStatus !== requestStatus.loading && countries.length <= 0) {
      dispatch(geoSlice.actions.getCountries());
    }
  }, []);

  useEffect(() => {
    if (planOptions.length <= 0) {
      dispatch(planSlice.actions.listAll());
    }
  }, []);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Navegar a la lista después de una edición exitosa
  useEffect(() => {
    if (previousStatus.current === requestStatus.loading && status === requestStatus.success) {
      navigate(routes.listClient.path);
    }
    previousStatus.current = status;
  }, [status, navigate]);

  const onEdit = useCallback(
    (values) => {
      const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
      // Los multi-selects platforms/plans se traducen a los escalares que acepta el backend.
      if ('platforms' in fieldsToUpdate || 'plans' in fieldsToUpdate) {
        delete fieldsToUpdate.platforms;
        delete fieldsToUpdate.plans;
        Object.assign(fieldsToUpdate, derivePlanPlatformInput(values.platforms, values.plans, planOptions));
      }
      const dataToSave = getDataToSave(fieldsToUpdate, 'user');
      dispatch(clientSlice.actions.update({ id, input: dataToSave }));
    },
    [dispatch, id, initialValues, planOptions]
  );

  const onFormChange = (formState) => {
    const { dirtyFields, values } = formState;
    const dirtyKeys = Object.keys(dirtyFields);

    // On country change, get the list of all country's states and reset the states and city fields
    if (values.country && values.country.label && values.country.label !== selectedCountry) {
      setSelectedCountry(values.country.label);
      dispatch(geoSlice.actions.getStates(values.country.label));

      if ('country' in dirtyKeys) {
        setSelectedState(null);

        if (values.state) formRef.current.change('state', null);
        if (values.city) formRef.current.change('city', null);
      }
    }

    // On state change, get the list of all state's cities and reset the city field
    if (values.state && values.state.value && values.state.value !== selectedState) {
      dispatch(
        geoSlice.actions.getCities({ state: values.state.value })
      );
      setSelectedState(values.state.value);

      if ('state' in dirtyKeys && values.city) formRef.current.change('city', null);
    }
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        mutators={{
          generatePassword
        }}
        onSubmit={onEdit}
        validate={validateEditClientForm}
        render={({ form, handleSubmit, invalid, pristine, values }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <FormSurface>
                {/* Cabecera premium unificada: avatar (click amplia) + nombre + rol. */}
                <UserEditHeader
                  photo={client?.photo}
                  firstName={client?.first_name}
                  lastName={client?.last_name}
                  email={client?.email}
                  roleLabel={t('roles.client', { ns: 'common', defaultValue: 'Cliente' })}
                />
                <ClientFields
                activePlans={client?.active_plans}
                cities={cities}
                countries={countries}
                form={form}
                geoStatus={geoStatus}
                planOptions={planOptions}
                planStatus={planStatus}
                requiredFields={requiredFields}
                showOrganization
                states={states}
                values={values}
                />
              </FormSurface>
              <StyledButtonsContainer>
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={status === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listClient.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('client.edit', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
              <FormSpy onChange={onFormChange} subscription={{ dirtyFields: true, values: true }} />
            </form>
          );
        }}
      />
    </>
  );
}

export default ClientEdit;
